import React, { useRef, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { RotateCcw, ZoomIn, ZoomOut, Play, Pause } from 'lucide-react';

interface Node {
  id: string;
  label: string;
  x: number;
  y: number;
  z: number;
  connections: string[];
  color: string;
  type: 'concept' | 'skill' | 'project';
}

interface Interactive3DModelProps {
  topic: string;
  nodes?: Node[];
}

export const Interactive3DModel: React.FC<Interactive3DModelProps> = ({ 
  topic,
  nodes: customNodes 
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [rotation, setRotation] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [isAnimating, setIsAnimating] = useState(false);
  const [selectedNode, setSelectedNode] = useState<Node | null>(null);

  // Default nodes for different topics
  const getDefaultNodes = (topic: string): Node[] => {
    switch (topic.toLowerCase()) {
      case 'react':
        return [
          { id: 'components', label: 'Components', x: 0, y: 0, z: 0, connections: ['jsx', 'props'], color: '#61dafb', type: 'concept' },
          { id: 'jsx', label: 'JSX', x: 100, y: 50, z: 0, connections: ['components'], color: '#f7df1e', type: 'concept' },
          { id: 'props', label: 'Props', x: -100, y: -50, z: 0, connections: ['components', 'state'], color: '#ff6b6b', type: 'concept' },
          { id: 'state', label: 'State', x: 0, y: -100, z: 50, connections: ['props', 'hooks'], color: '#4ecdc4', type: 'concept' },
          { id: 'hooks', label: 'Hooks', x: 0, y: 100, z: -50, connections: ['state'], color: '#45b7d1', type: 'skill' }
        ];
      case 'javascript':
        return [
          { id: 'variables', label: 'Variables', x: 0, y: 0, z: 0, connections: ['functions', 'objects'], color: '#f7df1e', type: 'concept' },
          { id: 'functions', label: 'Functions', x: 120, y: 0, z: 0, connections: ['variables', 'arrays'], color: '#ff6b6b', type: 'concept' },
          { id: 'objects', label: 'Objects', x: -120, y: 0, z: 0, connections: ['variables', 'arrays'], color: '#4ecdc4', type: 'concept' },
          { id: 'arrays', label: 'Arrays', x: 0, y: 120, z: 0, connections: ['functions', 'objects'], color: '#45b7d1', type: 'concept' },
          { id: 'async', label: 'Async/Await', x: 0, y: -120, z: 0, connections: [], color: '#96ceb4', type: 'skill' }
        ];
      default:
        return [
          { id: 'basics', label: 'Basics', x: 0, y: 0, z: 0, connections: ['intermediate'], color: '#61dafb', type: 'concept' },
          { id: 'intermediate', label: 'Intermediate', x: 100, y: 0, z: 0, connections: ['basics', 'advanced'], color: '#ff6b6b', type: 'concept' },
          { id: 'advanced', label: 'Advanced', x: 0, y: 100, z: 0, connections: ['intermediate'], color: '#4ecdc4', type: 'skill' }
        ];
    }
  };

  const nodes = customNodes || getDefaultNodes(topic);

  // Simple 3D to 2D projection
  const project3D = (x: number, y: number, z: number) => {
    const perspective = 400;
    const rotatedX = x * Math.cos(rotation.y) - z * Math.sin(rotation.y);
    const rotatedZ = x * Math.sin(rotation.y) + z * Math.cos(rotation.y);
    const rotatedY = y * Math.cos(rotation.x) - rotatedZ * Math.sin(rotation.x);
    
    const screenX = (rotatedX * perspective) / (perspective + rotatedZ) * zoom + 200;
    const screenY = (rotatedY * perspective) / (perspective + rotatedZ) * zoom + 150;
    
    return { x: screenX, y: screenY, depth: rotatedZ };
  };

  const drawScene = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Sort nodes by depth for proper rendering
    const projectedNodes = nodes.map(node => ({
      ...node,
      projected: project3D(node.x, node.y, node.z)
    })).sort((a, b) => a.projected.depth - b.projected.depth);

    // Draw connections
    projectedNodes.forEach(node => {
      node.connections.forEach(connId => {
        const connectedNode = projectedNodes.find(n => n.id === connId);
        if (connectedNode) {
          ctx.beginPath();
          ctx.moveTo(node.projected.x, node.projected.y);
          ctx.lineTo(connectedNode.projected.x, connectedNode.projected.y);
          ctx.strokeStyle = '#666';
          ctx.lineWidth = 2;
          ctx.stroke();
        }
      });
    });

    // Draw nodes
    projectedNodes.forEach(node => {
      const { x, y } = node.projected;
      const radius = 30 * zoom;
      
      // Node circle
      ctx.beginPath();
      ctx.arc(x, y, radius, 0, 2 * Math.PI);
      ctx.fillStyle = node.color;
      ctx.fill();
      ctx.strokeStyle = selectedNode?.id === node.id ? '#000' : '#333';
      ctx.lineWidth = selectedNode?.id === node.id ? 3 : 1;
      ctx.stroke();

      // Node label
      ctx.fillStyle = '#fff';
      ctx.font = `${12 * zoom}px Arial`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(node.label, x, y);
    });
  };

  React.useEffect(() => {
    drawScene();
  }, [rotation, zoom, selectedNode, nodes]);

  React.useEffect(() => {
    let animationId: number;
    
    if (isAnimating) {
      const animate = () => {
        setRotation(prev => ({ x: prev.x, y: prev.y + 0.01 }));
        animationId = requestAnimationFrame(animate);
      };
      animationId = requestAnimationFrame(animate);
    }
    
    return () => {
      if (animationId) cancelAnimationFrame(animationId);
    };
  }, [isAnimating]);

  const handleCanvasClick = (event: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const rect = canvas.getBoundingClientRect();
    const clickX = event.clientX - rect.left;
    const clickY = event.clientY - rect.top;
    
    // Find clicked node
    const clickedNode = nodes.find(node => {
      const projected = project3D(node.x, node.y, node.z);
      const distance = Math.sqrt(
        Math.pow(clickX - projected.x, 2) + Math.pow(clickY - projected.y, 2)
      );
      return distance <= 30 * zoom;
    });
    
    setSelectedNode(clickedNode || null);
  };

  const resetView = () => {
    setRotation({ x: 0, y: 0 });
    setZoom(1);
    setSelectedNode(null);
  };

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span>{topic} - 3D Knowledge Map</span>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsAnimating(!isAnimating)}
            >
              {isAnimating ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
            </Button>
            <Button variant="outline" size="sm" onClick={resetView}>
              <RotateCcw className="h-4 w-4" />
            </Button>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center gap-4">
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setZoom(Math.max(0.5, zoom - 0.1))}
            >
              <ZoomOut className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setZoom(Math.min(2, zoom + 0.1))}
            >
              <ZoomIn className="h-4 w-4" />
            </Button>
          </div>
          <Badge variant="secondary">Zoom: {Math.round(zoom * 100)}%</Badge>
        </div>

        <canvas
          ref={canvasRef}
          width={400}
          height={300}
          className="border rounded-lg cursor-pointer bg-background"
          onClick={handleCanvasClick}
          onMouseMove={(e) => {
            if (e.buttons === 1) { // Left mouse button held
              const movementX = e.movementX * 0.01;
              const movementY = e.movementY * 0.01;
              setRotation(prev => ({ 
                x: prev.x + movementY, 
                y: prev.y + movementX 
              }));
            }
          }}
        />

        {selectedNode && (
          <Card className="bg-muted">
            <CardContent className="pt-4">
              <div className="flex items-center gap-2 mb-2">
                <div 
                  className="w-4 h-4 rounded-full" 
                  style={{ backgroundColor: selectedNode.color }}
                />
                <h3 className="font-semibold">{selectedNode.label}</h3>
                <Badge variant="outline">{selectedNode.type}</Badge>
              </div>
              <p className="text-sm text-muted-foreground">
                Connected to: {selectedNode.connections.join(', ') || 'None'}
              </p>
            </CardContent>
          </Card>
        )}

        <div className="text-xs text-muted-foreground">
          💡 Click and drag to rotate • Click nodes to select • Use zoom controls
        </div>
      </CardContent>
    </Card>
  );
};